"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var Logger_1 = __importDefault(require("./Logger"));
var helpers_1 = require("./helpers");
var Connections_1 = require("./Connections");
var Connection_1 = require("./Connections/Connection");
var Pool = /** @class */ (function () {
    function Pool(serverUrl, protocol, ticket, algorithm, testId) {
        this.maxHttpConnections = 8;
        this.serverUrl = serverUrl;
        this.protocol = protocol;
        this.ticket = ticket;
        this.testId = testId;
        this.algorithm = algorithm || 'mixed';
        this.connections = {};
        Logger_1.default.log("Created new pool with algorithm: ".concat(this.algorithm, " and protocol: ").concat(this.protocol));
    }
    Pool.prototype.available = function () {
        return Object.values(this.connections).filter(function (item) { return item.free && !item.deleted; }).length;
    };
    Pool.prototype.availableWebsocketConnections = function () {
        return Object.values(this.connections).filter(function (item) { return item.free && !item.deleted && item.connection.type === 'websocket'; }).length;
    };
    Pool.prototype.availableHttpConnections = function () {
        return Object.values(this.connections).filter(function (item) { return item.free && !item.deleted && item.connection.type === 'http'; }).length;
    };
    Pool.prototype.reserved = function () {
        return Object.values(this.connections).filter(function (item) { return !item.free && !item.deleted; }).length;
    };
    Pool.prototype.reservedWebsocketConnections = function () {
        return Object.values(this.connections).filter(function (item) { return !item.free && !item.deleted && item.connection.type === 'websocket'; }).length;
    };
    Pool.prototype.reservedHttpConnections = function () {
        return Object.values(this.connections).filter(function (item) { return !item.free && !item.deleted && item.connection.type === 'http'; }).length;
    };
    Pool.prototype.createConnection = function (type) {
        if (type === void 0) { type = null; }
        var openWsConnections = this.reservedWebsocketConnections();
        var openHttpConnections = this.reservedHttpConnections();
        var choice = (0, helpers_1.algorithmChoice)(this.algorithm, openWsConnections, openHttpConnections, type, this.maxHttpConnections);
        if (choice === 'http') {
            return new Connections_1.HttpConnection(this.serverUrl, this.protocol, this.ticket, this.testId);
        }
        return new Connections_1.WebsocketConnection(this.serverUrl, this.protocol, this.ticket, this.testId);
    };
    Pool.prototype.reserveByType = function (wantedType) {
        var _this = this;
        if (wantedType === void 0) { wantedType = 'websocket'; }
        var connection = null;
        var type = wantedType;
        if (wantedType !== 'http' && this.algorithm === 'onlyHttp') {
            Logger_1.default.log("Wanted connection type (".concat(wantedType, ") overriden by algorithm (").concat(this.algorithm, ")"));
            type = 'http';
        }
        if (wantedType !== 'websocket' && this.algorithm === 'onlyWebsockets') {
            Logger_1.default.log("Wanted connection type (".concat(wantedType, ") overriden by algorithm (").concat(this.algorithm, ")"));
            type = 'websocket';
        }
        Object.entries(this.connections).forEach(function (_a) {
            var id = _a[0], item = _a[1];
            if (!connection && item.free && item.connection.type === type) {
                connection = item.connection;
                _this.connections[id].free = false;
            }
        });
        if (!connection) {
            connection = this.createConnection(type);
            this.resetListeners(connection);
            this.connections[connection.id] = { free: false, connection: connection };
        }
        return connection;
    };
    Pool.prototype.reserve = function (type) {
        if (type) {
            return this.reserveByType(type);
        }
        var connection = null;
        var chosenType = (0, helpers_1.algorithmChoice)(this.algorithm, this.reservedWebsocketConnections(), this.reservedHttpConnections(), type);
        Logger_1.default.log("Reserving connection by algorithm choice ".concat(chosenType, " with ").concat(this.availableWebsocketConnections(), " available websocket connections and ").concat(this.availableHttpConnections(), " available http connections."));
        if (chosenType) {
            connection = this.reserveByType(chosenType);
            Logger_1.default.log("Reserving idle connection ".concat(connection.id, " by type ").concat(chosenType));
        }
        if (!connection) {
            connection = this.createConnection();
            this.resetListeners(connection);
            this.connections[connection.id] = { free: false, connection: connection };
            Logger_1.default.log("No available connections. Creating new: ".concat(connection.id, " (").concat(connection.type, ")"));
        }
        return connection;
    };
    // eslint-disable-next-line class-methods-use-this
    Pool.prototype.resetListeners = function (connection) {
        connection.removeAllListeners(Connection_1.ConnectionEvents.OPEN);
        connection.removeAllListeners(Connection_1.ConnectionEvents.MESSAGE);
        connection.removeAllListeners(Connection_1.ConnectionEvents.CLOSE);
        connection.removeAllListeners(Connection_1.ConnectionEvents.ERROR);
    };
    Pool.prototype.release = function (id) {
        Logger_1.default.log("Wants to release ".concat(id));
        if (id in this.connections && !this.connections[id].free) {
            var item = this.connections[id];
            Logger_1.default.log("Connection exists with readyState ".concat(item.connection.readyState()));
            if (item.connection.readyState() !== 1) {
                this.remove(id);
                return;
            }
            // Reset listeners
            this.resetListeners(item.connection);
            this.connections[id].free = true;
            Logger_1.default.log("Released connection ".concat(id, " (").concat(item.connection.type, ")"));
        }
    };
    Pool.prototype.remove = function (id) {
        Logger_1.default.log("Wants to remove ".concat(id));
        if (id in this.connections) {
            var item = this.connections[id];
            // Reset listeners
            this.resetListeners(item.connection);
            this.connections[id].deleted = true;
            Logger_1.default.log("Removed connection ".concat(id, " (").concat(item.connection.type, ")"));
        }
    };
    Pool.prototype.closeAll = function (cb) {
        var _this = this;
        Logger_1.default.log('Closing all connections');
        Object.entries(this.connections).forEach(function (_a) {
            var id = _a[0], item = _a[1];
            if (item.connection.bufferedAmount() <= 0) {
                // Reset listeners
                _this.resetListeners(item.connection);
                item.connection.close();
                _this.connections[id].deleted = true;
                Logger_1.default.log("Closed connection ".concat(id, " (").concat(item.connection.type, ")"));
            }
            else {
                Logger_1.default.log("Can't close connection ".concat(id, " (").concat(item.connection.type, ") because of buffered amount (").concat(item.connection.bufferedAmount(), ")"));
            }
        });
        if (this.available() > 0) {
            setTimeout(function () {
                _this.closeAll(cb);
            }, 250);
        }
        else {
            cb();
        }
    };
    return Pool;
}());
exports.default = Pool;
//# sourceMappingURL=Pool.js.map